import { useState } from 'react';
import { CheckCircle2, PlusCircle, RotateCcw } from 'lucide-react';

import { showToast } from '@/utils/toast';
import { usePriceIntelStore } from '../stores/price-intel-store';
import type { PriceIntelManualCandidateInput } from '../types';

type ManualFormState = {
  source_name: string;
  competitor: string;
  series: string;
  model: string;
  pitch_mm: string;
  brightness: string;
  indoor_outdoor: string;
  application: string;
  region: string;
  country: string;
  price_per_piece: string;
  price_per_sqm: string;
  currency: string;
  year_quarter: string;
  notes: string;
  source_excerpt: string;
};

const emptyForm: ManualFormState = {
  source_name: '',
  competitor: '',
  series: '',
  model: '',
  pitch_mm: '',
  brightness: '',
  indoor_outdoor: '',
  application: '',
  region: '',
  country: '',
  price_per_piece: '',
  price_per_sqm: '',
  currency: 'USD',
  year_quarter: '',
  notes: '',
  source_excerpt: '',
};

const inputStyle: React.CSSProperties = {
  borderColor: 'var(--color-system-quaternary)',
  backgroundColor: 'var(--color-background-primary)',
  color: 'var(--color-system-primary)',
};

const mutedText: React.CSSProperties = { color: 'var(--color-system-secondary)' };

const primaryButton: React.CSSProperties = {
  backgroundColor: 'var(--color-key)',
  color: '#ffffff',
};

const ghostButton: React.CSSProperties = {
  backgroundColor: 'var(--color-background-primary)',
  borderColor: 'var(--color-system-quaternary)',
  borderWidth: 1,
  borderStyle: 'solid',
  color: 'var(--color-system-secondary)',
};

function toNumberOrNull(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

function toIntOrNull(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const n = Number.parseInt(trimmed, 10);
  return Number.isFinite(n) ? n : null;
}

interface ManualFactFormProps {
  onSuccess?: (candidateId: string) => void;
}

export function ManualFactForm({ onSuccess }: ManualFactFormProps) {
  const { createManualCandidate, isLoading } = usePriceIntelStore();
  const [form, setForm] = useState<ManualFormState>(emptyForm);

  const updateField = (key: keyof ManualFormState, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const validate = (): string | null => {
    if (!form.source_name.trim()) return '请先填写"来源标题"，便于后续审核检索。';
    if (!form.competitor.trim()) return '"竞品名称"是必填项。';
    const pricePiece = toNumberOrNull(form.price_per_piece);
    const priceSqm = toNumberOrNull(form.price_per_sqm);
    if (pricePiece === null && priceSqm === null) return '至少填写"件价"或"方价"中的一项。';
    return null;
  };

  const handleSubmit = async () => {
    const error = validate();
    if (error) {
      showToast(error);
      return;
    }

    const payload: PriceIntelManualCandidateInput = {
      source_name: form.source_name.trim(),
      competitor: form.competitor.trim(),
      series: form.series.trim() || null,
      model: form.model.trim() || null,
      pitch_mm: toNumberOrNull(form.pitch_mm),
      brightness: toIntOrNull(form.brightness),
      indoor_outdoor: form.indoor_outdoor || null,
      application: form.application.trim() || null,
      region: form.region.trim() || null,
      country: form.country.trim() || null,
      price_per_piece: toNumberOrNull(form.price_per_piece),
      price_per_sqm: toNumberOrNull(form.price_per_sqm),
      currency: form.currency.trim() || null,
      year_quarter: form.year_quarter.trim() || null,
      notes: form.notes.trim() || null,
      source_excerpt: form.source_excerpt.trim() || null,
      metadata: { intake: 'manual_entry' },
    };

    try {
      const candidate = await createManualCandidate(payload);
      showToast('已录入待审核候选事实');
      setForm((prev) => ({
        ...emptyForm,
        currency: prev.currency,
        year_quarter: prev.year_quarter,
        region: prev.region,
        country: prev.country,
      }));
      onSuccess?.(candidate.id);
    } catch (err) {
      showToast(`录入失败: ${(err as Error).message}`);
    }
  };

  const handleReset = () => {
    setForm(emptyForm);
  };

  return (
    <div className="space-y-5">
      <div
        className="rounded-lg border p-4"
        style={{ borderColor: 'var(--color-system-quaternary)', backgroundColor: 'var(--color-background-secondary)' }}
      >
        <div className="flex items-start gap-2 text-xs leading-6" style={mutedText}>
          <CheckCircle2 size={14} className="mt-0.5 shrink-0" style={{ color: 'var(--color-success)' }} />
          <span>
            手工录入会<strong style={{ color: 'var(--color-system-primary)' }}>跳过 AI 抽取</strong>，直接生成一条待审核的候选事实。
            保存后到「审核台」二次确认并点击「发布」即可进入正式事实表。
          </span>
        </div>
      </div>

      <section>
        <SectionTitle label="来源信息" />
        <div className="grid gap-3 md:grid-cols-2">
          <Field label="来源标题 *" hint="示例：2026Q2 中东客户现场报价">
            <input
              className="w-full rounded-lg border px-3 py-2 text-sm outline-none"
              style={inputStyle}
              value={form.source_name}
              onChange={(e) => updateField('source_name', e.target.value)}
              placeholder="便于审核检索的标题"
            />
          </Field>
          <Field label="原始线索摘录（可选）" hint="贴一句销售/邮件原文，审核时方便核对">
            <input
              className="w-full rounded-lg border px-3 py-2 text-sm outline-none"
              style={inputStyle}
              value={form.source_excerpt}
              onChange={(e) => updateField('source_excerpt', e.target.value)}
              placeholder="例如：沙特客户报 FX-Pro P2.6 $3200/sqm"
            />
          </Field>
        </div>
      </section>

      <section>
        <SectionTitle label="竞品与产品" />
        <div className="grid gap-3 md:grid-cols-2">
          <Field label="竞品名称 *">
            <input
              className="w-full rounded-lg border px-3 py-2 text-sm outline-none"
              style={inputStyle}
              value={form.competitor}
              onChange={(e) => updateField('competitor', e.target.value)}
              placeholder="Roe Visual / Absen / Infiled ..."
            />
          </Field>
          <Field label="系列 Series">
            <input
              className="w-full rounded-lg border px-3 py-2 text-sm outline-none"
              style={inputStyle}
              value={form.series}
              onChange={(e) => updateField('series', e.target.value)}
              placeholder="例如：Verity / Aries"
            />
          </Field>
          <Field label="型号 Model">
            <input
              className="w-full rounded-lg border px-3 py-2 text-sm outline-none"
              style={inputStyle}
              value={form.model}
              onChange={(e) => updateField('model', e.target.value)}
              placeholder="例如：BM2.6"
            />
          </Field>
          <Field label="间距 Pitch (mm)">
            <input
              className="w-full rounded-lg border px-3 py-2 text-sm outline-none"
              style={inputStyle}
              type="number"
              step="0.001"
              value={form.pitch_mm}
              onChange={(e) => updateField('pitch_mm', e.target.value)}
              placeholder="2.6"
            />
          </Field>
          <Field label="亮度 Brightness (nit)">
            <input
              className="w-full rounded-lg border px-3 py-2 text-sm outline-none"
              style={inputStyle}
              type="number"
              value={form.brightness}
              onChange={(e) => updateField('brightness', e.target.value)}
              placeholder="1200"
            />
          </Field>
          <Field label="室内 / 户外">
            <select
              className="w-full rounded-lg border px-3 py-2 text-sm outline-none"
              style={inputStyle}
              value={form.indoor_outdoor}
              onChange={(e) => updateField('indoor_outdoor', e.target.value)}
            >
              <option value="">—</option>
              <option value="indoor">室内 Indoor</option>
              <option value="outdoor">户外 Outdoor</option>
              <option value="rental">租赁 Rental</option>
              <option value="fixed">固装 Fixed</option>
            </select>
          </Field>
          <Field label="应用场景">
            <input
              className="w-full rounded-lg border px-3 py-2 text-sm outline-none"
              style={inputStyle}
              value={form.application}
              onChange={(e) => updateField('application', e.target.value)}
              placeholder="Broadcast / XR / Retail ..."
            />
          </Field>
          <Field label="季度">
            <input
              className="w-full rounded-lg border px-3 py-2 text-sm outline-none"
              style={inputStyle}
              value={form.year_quarter}
              onChange={(e) => updateField('year_quarter', e.target.value)}
              placeholder="2026Q2"
            />
          </Field>
        </div>
      </section>

      <section>
        <SectionTitle label="价格" />
        <div className="grid gap-3 md:grid-cols-3">
          <Field label="件价 Per Piece" hint="按箱/按模组价">
            <input
              className="w-full rounded-lg border px-3 py-2 text-sm outline-none"
              style={inputStyle}
              type="number"
              step="0.01"
              value={form.price_per_piece}
              onChange={(e) => updateField('price_per_piece', e.target.value)}
              placeholder="例如：850"
            />
          </Field>
          <Field label="方价 Per Sqm">
            <input
              className="w-full rounded-lg border px-3 py-2 text-sm outline-none"
              style={inputStyle}
              type="number"
              step="0.01"
              value={form.price_per_sqm}
              onChange={(e) => updateField('price_per_sqm', e.target.value)}
              placeholder="例如：3200"
            />
          </Field>
          <Field label="币种">
            <select
              className="w-full rounded-lg border px-3 py-2 text-sm outline-none"
              style={inputStyle}
              value={form.currency}
              onChange={(e) => updateField('currency', e.target.value)}
            >
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="CNY">CNY</option>
              <option value="GBP">GBP</option>
              <option value="JPY">JPY</option>
            </select>
          </Field>
        </div>
      </section>

      <section>
        <SectionTitle label="地区 & 备注" />
        <div className="grid gap-3 md:grid-cols-2">
          <Field label="区域 Region">
            <input
              className="w-full rounded-lg border px-3 py-2 text-sm outline-none"
              style={inputStyle}
              value={form.region}
              onChange={(e) => updateField('region', e.target.value)}
              placeholder="MEA / EMEA / APAC ..."
            />
          </Field>
          <Field label="国家 Country">
            <input
              className="w-full rounded-lg border px-3 py-2 text-sm outline-none"
              style={inputStyle}
              value={form.country}
              onChange={(e) => updateField('country', e.target.value)}
              placeholder="Saudi Arabia"
            />
          </Field>
          <Field label="备注" hint="客户背景、项目节点、交期等">
            <textarea
              className="w-full rounded-lg border px-3 py-2 text-sm outline-none"
              style={inputStyle}
              rows={3}
              value={form.notes}
              onChange={(e) => updateField('notes', e.target.value)}
              placeholder="可留空"
            />
          </Field>
        </div>
      </section>

      <div className="flex items-center justify-between gap-3 pt-2">
        <div className="text-[11px] uppercase tracking-[0.2em]" style={mutedText}>
          candidate only · pending review
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleReset}
            className="inline-flex items-center rounded-lg px-3 py-2 text-sm font-medium transition hover:opacity-80"
            style={ghostButton}
          >
            <RotateCcw size={14} className="mr-1.5" />
            重置
          </button>
          <button
            type="button"
            disabled={isLoading}
            onClick={() => void handleSubmit()}
            className="inline-flex items-center rounded-lg px-4 py-2 text-sm font-medium transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            style={primaryButton}
          >
            <PlusCircle size={15} className="mr-1.5" />
            保存并送审
          </button>
        </div>
      </div>
    </div>
  );
}

function SectionTitle({ label }: { label: string }) {
  return (
    <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.22em]" style={mutedText}>
      {label}
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="mb-1.5 text-xs font-medium" style={{ color: 'var(--color-system-primary)' }}>
        {label}
      </div>
      {children}
      {hint && (
        <div className="mt-1 text-[11px]" style={mutedText}>
          {hint}
        </div>
      )}
    </label>
  );
}
